import { WorkCase } from '@/data/types';

const aiVideoTranscriptionAutomation: WorkCase = {
  id: "video-transcription",
  slug: "ai-video-transcription-automation",
  title: "AI Video Transcription",
  description: "Automated solution for creating accessible subtitles for thousands of product videos in an online shop.",
  tags: ["Python", "OpenAI Whisper", "OpenCV", "FFmpeg", "Web Crawling", "AI", "Subtitles", "BFSG"],
  image: "/assets/img/work/ai_transcripts_python.webp",
  articleType: "CASE STUDY",
  date: "2025-03",
  detailContent: {
    subtitle: "AI-powered subtitling and accessibility for e-commerce video content",
    challenge: [
      {
        text: "The product videos of an online shop needed better accessibility through transcription and subtitles, a requirement that has gained relevance with Germany's Accessibility Strengthening Act (BFSG)."
      },
      {
        text: "The scope was substantial: thousands of product videos had to be identified, retrieved, and equipped with precise subtitles. Manual processing would have been far too time-consuming and expensive. On top of that, the video files were not listed in any central overview and had to be located first."
      },
      {
        text: "Additional challenges included:"
      },
      {
        listing: [
          "The lack of a central video overview or any clear structure of the storage locations",
          "Protection measures against automated access, which required careful, rate-limited retrieval",
          "The need for word-accurate subtitles for optimal accessibility",
          "Required adjustments for brand names and industry-specific terminology"
        ]
      }
    ],
    solution: [
      {
        text: "I built an end-to-end pipeline that solved the problem in several steps:"
      },
      {
        subheadline: "1. Crawler for video identification",
        text: "By analyzing the website structure, I discovered that the product videos followed a fixed naming scheme based on the respective product number. I built a Python crawler that systematically checked the catalog and identified over a thousand videos this way, without needing any central overview."
      },
      {
        subheadline: "2. Server-friendly download management",
        text: "For retrieval, I implemented a sequential, rate-limited process with delayed requests and clean session handling. This way the videos were downloaded reliably and without straining the servers."
      },
      {
        subheadline: "3. Audio extraction and AI transcription",
        text: "I used FFmpeg to extract the audio tracks from the videos. For transcription I used a specialized fork of OpenAI's Whisper (whisper-timestamped) that delivers word-accurate timestamps, a feature otherwise only available in the paid OpenAI API."
      },
      {
        subheadline: "4. Subtitle file generation",
        text: "I converted the word-accurate transcriptions into SRT and VTT formats. I implemented an intelligent subtitle segmentation algorithm that ensured they fit the screen well and appear in sync with the spoken words."
      },
      {
        subheadline: "5. Automatic thumbnail generation",
        text: "Using OpenCV, I analyzed the videos to extract high-quality frames as thumbnails. The algorithm took factors like color variety and level of detail into account to ensure the thumbnails are representative and appealing."
      }
    ],
    results: [
      {
        text: "The pipeline delivered impressive results:"
      },
      {
        listing: [
          "Successful identification and processing of over a thousand product videos",
          "Automatic creation of precise subtitles with an estimated error rate below 5% for regular text",
          "Significant accessibility improvement for hearing-impaired customers",
          "Enormous time and cost savings compared to manual transcription",
          "Reusable pipeline that can be applied to new product videos"
        ]
      },
      {
        text: "Particularly noteworthy are the quality of the automatically generated subtitles and the precision of the word timings, which enable a synchronized reading experience. The only limitation was the recognition of specific brand names and technical terms, which could be improved in future versions with customized dictionaries."
      }
    ],
    technologies: [
      "Python 3.10",
      "OpenAI Whisper (Fork)",
      "FFmpeg",
      "OpenCV",
      "WebVTT & SRT",
      "Requests & BeautifulSoup"
    ],
    codeExamples: [
      {
        language: "python",
        code: `class PreciseSubtitleGenerator:
    def __init__(self, input_dir="transcribed_videos", model_name="large-v3", device="auto", vad_method="auditok"):
        """
        Initializes the precise subtitle generator with whisper-timestamped.

        input_dir: The base directory containing the transcribed videos
        model_name: The Whisper model to use
        device: Device for processing ('auto', 'cpu', or 'mps')
        vad_method: VAD method ("silero", "auditok", "auditok:v3.1", False)
        """
        self.input_dir = input_dir
        self.model_name = model_name
        self.vad_method = vad_method
        self.model = None

        # Check VAD compatibility
        if vad_method == "silero" and not has_vad_deps:
            print("Warning: Silero VAD dependencies are missing. Using auditok instead.")
            self.vad_method = "auditok"

        # Automatic device selection - try CPU first as the safest option
        self.device = "cpu"
`,
        description: "The PreciseSubtitleGenerator class implements the core functionality for automatic subtitle generation with Whisper. It supports various hardware configurations and VAD methods."
      }
    ],
    images: [
      {
        src: "/assets/img/work/transcription_flow.webp",
        zoomSrc: "/assets/img/work/transcription_flow.svg",
        thumbSrc: "/assets/img/work/transcription_flow.webp",
        alt: "Transcription Workflow",
        type: "diagram",
        title: "Transcription Workflow",
        caption: "The workflow shows the video transcription process."
      },
      {
        type: "video",
        videoId: "01007470",
        title: "Product video with automatically generated subtitles",
        caption: "A product video with word-accurate subtitles improves accessibility for hearing-impaired users"
      }
    ]
  }
};

export default aiVideoTranscriptionAutomation;
